import React, { useEffect, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";

import { devPanelEnabledHere } from "../../dev/gating";
import { sandboxOn, useDevSession } from "../../dev/devSession";
import { useSessionRecorder } from "../../dev/hooks";
import { useWaveTuning } from "../board/waveTuning";
import { Toggle } from "./controls";
import { devStyles as s } from "./devStyles";
import ExportSection from "./ExportSection";
import InfoSection from "./InfoSection";
import PlaySection from "./PlaySection";
import SetupSection from "./SetupSection";
import ViewSection from "./ViewSection";
import WaveSection from "./WaveSection";

const TABS = ["Play", "Setup", "View", "Wave", "Info", "Export"] as const;
type Tab = (typeof TABS)[number];

/**
 * The staging/dev panel (shown in __DEV__, or with ?dev=1 / ?tune=1 off
 * production): a collapsed "Dev ▸" pill in the board's top-left corner that
 * opens into tabs. `onMove` is the board's tap handler; auto-play uses it.
 */
const DevPanel: React.FC<{ onMove?: (word: string) => void }> = ({
  onMove,
}) => {
  const [enabled] = useState(devPanelEnabledHere);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("Play");
  const init = useWaveTuning((st) => st.init);
  const sandbox = useDevSession(sandboxOn);
  useEffect(() => {
    if (enabled) init();
  }, [enabled, init]);
  useSessionRecorder(enabled);
  if (!enabled) return null;

  if (!open) {
    return (
      <Pressable
        testID="dev-panel-toggle"
        onPress={() => setOpen(true)}
        style={[s.box, s.pill]}
      >
        <Text style={[s.text, s.devTitle]}>Dev ▸</Text>
      </Pressable>
    );
  }

  return (
    <View testID="dev-panel" style={[s.box, s.panel]}>
      <Pressable testID="dev-panel-close" onPress={() => setOpen(false)}>
        <Text style={[s.text, s.devTitle]}>Dev ▾</Text>
      </Pressable>
      <Toggle
        testID="dev-sandbox"
        label="Sandbox (don't save to history/stats/achievements)"
        value={sandbox}
        onChange={(v) => useDevSession.getState().setSandbox(v)}
      />
      <View style={[s.row, s.wrap]}>
        {TABS.map((t) => (
          <Pressable
            key={t}
            testID={`dev-tab-${t}`}
            onPress={() => setTab(t)}
            style={[s.tab, tab === t && s.on]}
          >
            <Text style={s.text}>{t}</Text>
          </Pressable>
        ))}
      </View>
      <ScrollView style={[s.scroll, s.section]}>
        {tab === "Play" && <PlaySection onMove={onMove} />}
        {tab === "Setup" && <SetupSection />}
        {tab === "View" && <ViewSection />}
        {tab === "Wave" && <WaveSection />}
        {tab === "Info" && <InfoSection />}
        {tab === "Export" && <ExportSection />}
      </ScrollView>
    </View>
  );
};

export default DevPanel;
